/**
 * Teaching goal for the halogen state explorer (CT-INTERACT C7): the learner types a temperature
 * and `halogenStatesAt` gives the state of every halogen at 100 kPa. The tasks read only that
 * result, so they use the same reviewed melting and boiling points as the badges.
 */
import { halogenStatesAt, type HalogenId, type HalogenStateStep } from './groupReactivity';
import type { ExploreChallenge } from './modelInputContract';

/** Default temperature of the state explorer (room temperature). */
export const DEFAULT_HALOGEN_TEMPERATURE_C = 25;

/** Input to the teaching tasks: the learner's temperature with the states it produced. */
export interface HalogenStatesChallengeInput {
  temperatureC: number;
  states: HalogenStateStep[];
}

export function halogenStatesChallengeInput(temperatureC: number): HalogenStatesChallengeInput {
  return { temperatureC, states: halogenStatesAt(temperatureC) };
}

export const HALOGEN_STATES_QUESTION = 'Why are the halogens a gas, then a liquid, then a solid as you go down the group?';

/** Bromine boils at 59 °C; the task band is its boiling point up to 5 °C above it. */
const BROMINE_BOILING_POINT_C = 59;
const BOILING_BAND_C = 5;

function stateOf(input: HalogenStatesChallengeInput, id: HalogenId) {
  return input.states.find((entry) => entry.id === id)?.state;
}

/** Teaching tasks (CT-INTERACT C7). Each check reads only the temperature and the model states. */
export const HALOGEN_STATES_CHALLENGES: ExploreChallenge<HalogenStatesChallengeInput>[] = [
  {
    id: 'chlorine-liquid',
    prompt: 'Cool the halogens until chlorine is a liquid.',
    hint: 'Chlorine boils at −34 °C.',
    isMet: (input) => stateOf(input, 'Cl') === 'liquid',
    why: 'Chlorine is a gas in the room only because its boiling point (−34 °C) is below 25 °C. Between −101 °C and −34 °C it is a liquid: the state depends on the temperature, not on the element alone.',
  },
  {
    id: 'bromine-boils',
    prompt: 'Find the temperature where bromine boils: make bromine a gas, no more than 5 °C above its boiling point.',
    hint: 'Raise the temperature step by step and watch the bromine badge.',
    isMet: (input) =>
      stateOf(input, 'Br') === 'gas' && input.temperatureC <= BROMINE_BOILING_POINT_C + BOILING_BAND_C,
    why: 'Bromine boils at 59 °C. At that temperature fluorine and chlorine are already gases, but iodine is still a solid until 114 °C.',
  },
  {
    id: 'iodine-liquid',
    prompt: 'Melt iodine without boiling it: make iodine a liquid.',
    hint: 'Iodine melts at 114 °C and boils at 184 °C.',
    isMet: (input) => stateOf(input, 'I') === 'liquid',
    why: 'Iodine has the biggest molecules in the group, so the forces between its molecules are strongest and it needs 114 °C to melt. Down the group the boiling points rise: −188, −34, 59 and 184 °C.',
  },
];
